// Helpers to turn thesession.org fields into full, renderable ABC.
import abcjs from "abcjs";
import { transposeAbcText } from "./transpose";

const MODE_SUFFIX = {
  major: "",
  minor: "min",
  dorian: "dor",
  mixolydian: "mix",
  ionian: "",
  aeolian: "min",
  phrygian: "phr",
  lydian: "lyd",
  locrian: "loc",
};

export const keyToAbc = (mode) => {
  if (!mode) return "D";
  const root = mode[0];
  const rest = mode.slice(1).toLowerCase();
  const suffix = MODE_SUFFIX[rest] ?? "";
  return root + suffix;
};

// Collapse blank lines so a stray empty line doesn't terminate the ABC tune
// (in ABC a blank line ends the tune — this keeps everything the user typed).
export const cleanAbc = (abc) => (abc || "").replace(/\n[ \t]*\n+/g, "\n");

// Full ABC document (with headers) for editing / rendering a whole setting.
export const buildAbc = ({ name, type, key, meter, abc }) => {
  const header = [
    "X:1",
    name ? `T:${name}` : null,
    type ? `R:${type}` : null,
    `M:${meter || "4/4"}`,
    "L:1/8",
    `K:${keyToAbc(key)}`,
  ]
    .filter(Boolean)
    .join("\n");
  return `${header}\n${(abc || "").trim()}`;
};

// Compact one-line ABC for list previews (no title).
export const buildPreviewAbc = ({ key, meter, abc }) => {
  const firstLine = (abc || "").split(/\r?\n/)[0] || "";
  return `X:1\nM:${meter || "4/4"}\nL:1/8\nK:${keyToAbc(key)}\n${firstLine}`;
};

// Trim any stored/edited ABC down to its first music line for list previews.
export const firstLineAbc = (abc) => {
  const lines = cleanAbc(abc).split(/\r?\n/);
  const head = [];
  let body = "";
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (/^[A-Za-z]:/.test(t) || t.startsWith("%")) {
      if (!/^T:/.test(t)) head.push(t); // drop the title in previews
      continue;
    }
    body = t;
    break;
  }
  if (!head.some((h) => h.startsWith("X:"))) head.unshift("X:1");
  return `${head.join("\n")}\n${body}`;
};

export const prettyKey = (mode) => {
  if (!mode) return "";
  const root = mode[0];
  const rest = mode.slice(1);
  return `${root} ${rest}`;
};

// Transpose the ABC TEXT itself by `steps` semitones (Transpose > abc > notation).
export const transposeAbc = (abc, steps) => {
  try {
    return transposeAbcText(abc, steps);
  } catch (e) {
    return abc;
  }
};

// ---------------------------------------------------------------- line magnet
const BAR_RE = /(:\|\|:|\|\|:|:\|:|::|\|:|:\||\|\]|\[\||\|\||\|)/g;
const hasNotes = (s) => /[A-Ga-gzZxX]/.test(s.replace(/\[[A-Za-z]:[^\]]*\]/g, ""));

// Split a music line into measures, keeping each bar line attached to its measure.
export const splitMeasures = (line) => {
  const parts = [];
  let last = 0;
  let m;
  BAR_RE.lastIndex = 0;
  while ((m = BAR_RE.exec(line)) !== null) {
    parts.push(line.slice(last, m.index + m[0].length));
    last = m.index + m[0].length;
  }
  const tail = line.slice(last);
  if (tail.trim()) parts.push(tail);
  // merge pick-up fragments that carry no notes (e.g. a leading "|:") forward
  const out = [];
  let carry = "";
  parts.forEach((p) => {
    if (!hasNotes(p)) {
      carry += p;
      return;
    }
    out.push(carry + p);
    carry = "";
  });
  if (carry.trim() && out.length) out[out.length - 1] += carry;
  else if (carry.trim()) out.push(carry);
  // a lead-in / pick-up bar belongs to the first full measure, not its own row
  const noteCount = (s) => (s.match(/[A-Ga-gz]/g) || []).length;
  if (out.length > 1 && noteCount(out[0]) <= 2) {
    out[1] = out[0] + out[1];
    out.shift();
  }
  return out;
};

const isStrongEnd = (measure) => /(:\||\|\||\|\]|::)\s*$/.test(measure);

// Split one source line into as few balanced rows as the zoom allows, snapping to
// repeat/section bar lines when one sits within a measure of the ideal cut.
const chunkMeasures = (segs, capacity, tol) => {
  const n = segs.length;
  if (n <= capacity + tol) return [segs];
  const parts = Math.ceil(n / capacity);
  const target = Math.ceil(n / parts);
  const out = [];
  let i = 0;
  while (i < n) {
    let end = Math.min(n, i + target);
    for (let j = Math.min(n - 1, i + target + 1); j >= Math.max(i + 1, i + target - 1); j--) {
      if (isStrongEnd(segs[j - 1])) {
        end = j;
        break;
      }
    }
    out.push(segs.slice(i, end));
    i = end;
  }
  return out;
};

// Re-flow an ABC tune so printed rows follow the tune's OWN line breaks whenever
// they fit `capacity` measures (±tol of "magnet" resistance); only when a source
// line is far too long is it split, preferring repeat/section bar lines.
export const magnetAbc = (abc, capacity, tol = 1) => {
  const lines = cleanAbc(abc).split(/\r?\n/);
  const out = [];
  lines.forEach((line) => {
    const t = line.trim();
    if (!t || /^[A-Za-z]:/.test(t) || t.startsWith("%")) {
      out.push(line);
      return;
    }
    const segs = splitMeasures(line);
    if (segs.length === 0) {
      out.push(line);
      return;
    }
    chunkMeasures(segs, Math.max(1, capacity), tol).forEach((c) => out.push(c.join("").trim()));
  });
  return out.join("\n");
};

// Total number of measures in the tune body (used to size the print capacity).
export const countMeasures = (abc) =>
  cleanAbc(abc)
    .split(/\r?\n/)
    .filter((l) => l.trim() && !/^[A-Za-z]:/.test(l.trim()) && !l.trim().startsWith("%"))
    .reduce((n, l) => n + splitMeasures(l).length, 0);
